import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { isNativeApp } from "@/lib/utils";
import { Search, Heart, Bell, Plus, User, Menu, X, ShoppingBag } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { unread } = useNotifications() || { unread: 0 };
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hideBar, setHideBar] = useState(false);

  // Mode app : l'en-tête se masque en scrollant vers le bas, réapparaît en remontant
  useEffect(() => {
    if (!isNativeApp()) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 6) return;
      setHideBar(y > 90 && y > last);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Close user menu on outside click + on Esc + on route change
  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setUserMenuOpen(false); };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/catalogue?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className={`sticky top-0 z-40 bg-white hairline-b app-header-anim ${hideBar ? "app-header-hide" : ""}`}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center gap-3 xl:gap-5 py-3 lg:py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 -my-2" data-testid="brand-logo">
            <img src="/logo-fripri-full.svg" alt="Fripri" className="h-16 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-4 ml-2">
            <NavLink to="/catalogue?category=vetements" className="label font-bold hover:text-[color:var(--color-coral)]" data-testid="nav-vetements">Vêtements</NavLink>
            <NavLink to="/catalogue?category=chaussures" className="label font-bold hover:text-[color:var(--color-coral)]" data-testid="nav-chaussures">Chaussures</NavLink>
            <NavLink to="/catalogue?category=sacs" className="label font-bold hover:text-[color:var(--color-coral)]" data-testid="nav-sacs">Sacs</NavLink>
            <NavLink to="/catalogue?category=accessoires" className="label font-bold hover:text-[color:var(--color-coral)]" data-testid="nav-accessoires">Accessoires</NavLink>
            <NavLink to="/encheres" className="label font-bold hover:text-[color:var(--color-coral)]" data-testid="nav-encheres">Enchères</NavLink>
            <NavLink to="/brocante-live" className="label font-bold whitespace-nowrap text-[color:var(--color-coral)]" data-testid="nav-brocante-live">● Brocante Live</NavLink>
            <NavLink to="/pro" className="label font-bold whitespace-nowrap hover:text-[color:var(--color-coral)]" data-testid="nav-pro">Fripri Pro 💼</NavLink>
          </nav>

          {/* Search */}
          <form onSubmit={onSearch} className="flex-1 min-w-0 hidden xl:flex items-center bg-[color:var(--color-surface)] px-3" data-testid="search-form">
            <Search size={16} strokeWidth={2.4} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une marque, un style…"
              className="bg-transparent py-2.5 outline-none flex-1 min-w-0 text-sm px-2 font-medium"
            />
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3 lg:gap-4">
            <Link to="/vendre" className="btn btn-mint btn-sm hidden xl:inline-flex" data-testid="header-sell-btn">
              <Plus size={14} strokeWidth={3} /> Vendre
            </Link>
            <Link to="/notifications" className="relative" data-testid="nav-notifications">
              <Bell size={20} strokeWidth={2} />
              {unread > 0 && <span className="dot" data-testid="notification-dot"/>}
            </Link>
            <Link to="/favoris" className="hidden xl:block" data-testid="nav-favoris"><Heart size={20} strokeWidth={2} /></Link>
            <Link to="/panier" data-testid="nav-panier"><ShoppingBag size={20} strokeWidth={2} /></Link>
            {user ? (
              <div ref={userMenuRef} className="relative hidden xl:block">
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center gap-2"
                  data-testid="user-menu"
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                    {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "U").toUpperCase()}
                  </div>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border-2 border-black z-50" onClick={() => setUserMenuOpen(false)}>
                    <Link to="/dashboard" className="block px-4 py-3 text-sm font-medium hover:bg-[color:var(--color-mint)]" data-testid="menu-dashboard">Mon espace</Link>
                    <Link to="/vendre" className="block px-4 py-3 text-sm font-medium hover:bg-[color:var(--color-mint)]">+ Vendre un article</Link>
                    <Link to="/messages" className="block px-4 py-3 text-sm font-medium hover:bg-[color:var(--color-mint)]">Messages</Link>
                    <Link to="/notifications" className="block px-4 py-3 text-sm font-medium hover:bg-[color:var(--color-mint)]">Notifications</Link>
                    {user.role === "agent" && (
                      <Link to="/agent" className="block px-4 py-3 text-sm font-bold bg-[color:var(--color-mint)] hover:bg-black hover:text-[color:var(--color-mint)]" data-testid="menu-agent">🤝 Espace Agent</Link>
                    )}
                    {user.role === "admin" && (
                      <Link to="/admin" className="block px-4 py-3 text-sm font-bold bg-black text-[color:var(--color-mint)] hover:bg-[color:var(--color-mint)] hover:text-black" data-testid="menu-admin">⚡ Admin</Link>
                    )}
                    {(user.role === "logistics" || user.role === "admin") && (
                      <Link to="/dirlog" className="block px-4 py-3 text-sm font-bold bg-[color:var(--color-yellow)] hover:bg-black hover:text-[color:var(--color-yellow)]" data-testid="menu-logistics">🚚 Logistique</Link>
                    )}
                    <button onClick={logout} className="w-full text-left px-4 py-3 text-sm font-medium hover:bg-[color:var(--color-coral)] hover:text-white" data-testid="logout-btn">Se déconnecter</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm hidden xl:inline-flex" data-testid="nav-login">Connexion</Link>
            )}
            <button onClick={() => setOpen(!open)} className="xl:hidden" data-testid="mobile-menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden pb-4 space-y-1 fade-in">
            <form onSubmit={onSearch} className="flex items-center bg-[color:var(--color-surface)] px-3 mb-3">
              <Search size={16} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="bg-transparent py-2.5 outline-none flex-1 text-sm px-2" data-testid="mobile-search-input" />
            </form>
            <Link onClick={() => setOpen(false)} to="/catalogue?category=vetements" className="block py-2 label font-bold" data-testid="mobile-nav-vetements">Vêtements</Link>
            <Link onClick={() => setOpen(false)} to="/catalogue?category=chaussures" className="block py-2 label font-bold" data-testid="mobile-nav-chaussures">Chaussures</Link>
            <Link onClick={() => setOpen(false)} to="/catalogue?category=sacs" className="block py-2 label font-bold" data-testid="mobile-nav-sacs">Sacs</Link>
            <Link onClick={() => setOpen(false)} to="/catalogue?category=accessoires" className="block py-2 label font-bold" data-testid="mobile-nav-accessoires">Accessoires</Link>
            <Link onClick={() => setOpen(false)} to="/manifeste" className="block py-2 label font-bold" data-testid="mobile-nav-manifeste">Manifeste</Link>
            <Link onClick={() => setOpen(false)} to="/brocante-live" className="block py-2 label font-bold text-[color:var(--color-coral)]" data-testid="mobile-nav-brocante-live">● Brocante Live</Link>
            <Link onClick={() => setOpen(false)} to="/pro" className="block py-2 label font-bold" data-testid="mobile-nav-pro">Fripri Pro 💼</Link>
            {user?.role === "agent" && (
              <Link onClick={() => setOpen(false)} to="/agent" className="block py-2 label font-bold">🤝 Espace Agent</Link>
            )}
            {user ? (
              <>
                <Link onClick={() => setOpen(false)} to="/dashboard" className="block py-2 label font-bold">Mon espace</Link>
                <button onClick={() => { setOpen(false); logout(); }} className="block py-2 label font-bold text-[color:var(--color-coral)]">Se déconnecter</button>
              </>
            ) : (
              <Link onClick={() => setOpen(false)} to="/login" className="block py-2 label font-bold" data-testid="mobile-nav-login">Connexion</Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
